import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { workspaceApi, WorkspaceStateResponse } from '../services/workspaceApi';
import { clientsApi } from '../services/clientsApi';
import api from '../services/api';

interface WorkspaceContextType {
  clientId: string | null;
  clientName: string | null;
  recentWorkspaces: WorkspaceStateResponse[];
  switchWorkspace: (clientId: string | null, clientName?: string | null) => void;
  refreshRecent: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  clientId: null,
  clientName: null,
  recentWorkspaces: [],
  switchWorkspace: () => {},
  refreshRecent: async () => {},
});

export const useWorkspace = () => useContext(WorkspaceContext);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [recentWorkspaces, setRecentWorkspaces] = useState<WorkspaceStateResponse[]>([]);

  const refreshRecent = useCallback(async () => {
    try {
      const recent = await workspaceApi.listRecentWorkspaces(5);
      setRecentWorkspaces(recent);
    } catch {
      // silently fail — recent list is a convenience, not critical
    }
  }, []);

  const switchWorkspace = useCallback((newClientId: string | null, newClientName?: string | null) => {
    setClientId(newClientId);
    setClientName(newClientName ?? null);
    // Path-tracking is handled by the location-watching effect below — don't
    // write `last_visited_path` here. Writing on switch would record the
    // workspace home itself, which makes the Resume card a no-op (it would
    // navigate the lawyer back to the page he's already on).
    refreshRecent();
  }, [refreshRecent]);

  // Inject X-Workspace-Client-Id header on every request
  useEffect(() => {
    const interceptorId = api.interceptors.request.use((config) => {
      if (clientId) {
        config.headers['X-Workspace-Client-Id'] = clientId;
      } else {
        delete config.headers['X-Workspace-Client-Id'];
      }
      return config;
    });
    return () => {
      api.interceptors.request.eject(interceptorId);
    };
  }, [clientId]);

  // Load recent workspaces on mount
  useEffect(() => {
    refreshRecent();
  }, [refreshRecent]);

  // Auto-switch the workspace when a tool page is deep-linked with ?client_id=X.
  // Without this, the sidebar pill would still point at the previous client
  // while the banner + X-Workspace-Client-Id header silently disagree.
  //
  // Guards against two loop modes:
  //   (a) URL identical across renders — `lastAutoSwitchedRef` dedupes
  //       successful switches.
  //   (b) Fetch 403/404 — on failure we revert clientId to null AND record the
  //       id in `failedSwitchesRef` so we don't keep retrying. Without this,
  //       the effect would re-enter because url !== null-state and loop.
  const location = useLocation();
  const lastAutoSwitchedRef = useRef<string | null>(null);
  const failedSwitchesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlClientId = params.get('client_id');
    if (!urlClientId) return;
    if (urlClientId === clientId) return;
    if (lastAutoSwitchedRef.current === urlClientId) return;
    if (failedSwitchesRef.current.has(urlClientId)) return;
    lastAutoSwitchedRef.current = urlClientId;

    setClientId(urlClientId);
    setClientName(null);
    // Path-tracking happens in the dedicated effect below — don't write here.
    clientsApi
      .getClient(urlClientId)
      .then((c) => setClientName(c?.display_name ?? null))
      .catch(() => {
        // Not accessible (403/404) — revert AND block future retries for this
        // id. Without the failed-set, the effect would reenter on the next
        // render (clientId=null, urlClientId=X) and loop.
        failedSwitchesRef.current.add(urlClientId);
        setClientId(null);
        setClientName(null);
      });
    refreshRecent();
  }, [location.search, clientId, refreshRecent]);

  // Path-tracking: when the lawyer is in a client context AND visits a tool
  // page (FIR Analyzer, Bail Calculator, Theory Builder, Vault, etc.), persist
  // the path so the Resume card on the workspace home can deep-link back.
  //
  // Skipped paths intentionally do not update last_visited_path:
  //   - /workspace/*  — the workspace home itself; would make Resume a no-op.
  //   - /clients, /clients/new — client-management surfaces, not "work for X".
  //   - /, /settings, /profile, /admin* — not client-scoped.
  // Everything else is a tool surface and is recorded.
  const lastWrittenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!clientId) return;
    const path = location.pathname;
    if (
      path === '/' ||
      path.startsWith('/workspace/') ||
      path.startsWith('/clients') ||
      path.startsWith('/settings') ||
      path.startsWith('/profile') ||
      path.startsWith('/admin')
    ) {
      return;
    }
    const key = `${clientId}::${path}`;
    if (lastWrittenRef.current === key) return;
    lastWrittenRef.current = key;
    workspaceApi.updateWorkspaceState(clientId, path).catch(() => {});
  }, [clientId, location.pathname]);

  return (
    <WorkspaceContext.Provider value={{ clientId, clientName, recentWorkspaces, switchWorkspace, refreshRecent }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
